import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, Ban } from 'lucide-react';
import type { VideoAssignment } from '@platform/shared-types';
import { videoTasksApi, type VideoTaskCounts } from '../../services/videoTasksApi';
import { academyApi, type ModuleWithChapters } from '../../services/academyApi';
import { api } from '../../services/apiService';
import type { Tenant, User } from '../../types';
import SearchableDropdown from '../../components/SearchableDropdown';
import { DatePicker } from '../../components/DatePicker';
import { ConfirmDialog } from '../../components/finance/financeUi';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import VideoTaskReview from './VideoTaskReview';
import {
  STATUS_FILTERS, STATUS_LABEL, STATUS_TONE, SURFACE_LABEL,
  isOverdue, formatDue, dueFromDate, dateFromDue,
} from './videoTaskUi';

type Surface = VideoAssignment['surface'];

interface FormState {
  title: string;
  brief: string;
  keyPoints: string[];
  surface: Surface;
  moduleId: string | null;
  chapterId: string | null;
  dueDate: string;
  tenantId: string | null;
  userId: string | null;
  rewardTokens: string;
}

const EMPTY_FORM: FormState = {
  title: '', brief: '', keyPoints: [], surface: 'platform-app',
  moduleId: null, chapterId: null, dueDate: '', tenantId: null, userId: null, rewardTokens: '0',
};

const formFrom = (a: VideoAssignment): FormState => ({
  title: a.title,
  brief: a.brief,
  keyPoints: [...a.keyPoints],
  surface: a.surface,
  moduleId: a.target?.moduleId ?? null,
  chapterId: a.target?.chapterId ?? null,
  dueDate: dateFromDue(a.dueAt),
  tenantId: a.assignee?.tenantId ?? null,
  userId: a.assignee?.userId ?? null,
  rewardTokens: String(a.rewardTokens ?? 0),
});

/** Rows that open the Review screen instead of the edit form. */
const REVIEWABLE = new Set(['submitted', 'approved']);
/** Only these statuses accept the `assign` event (see VIDEO_TASK edges). */
const ASSIGNABLE = new Set(['draft', 'assigned']);
const LOCKED = new Set(['approved', 'cancelled']);

const inputCls = 'w-full min-h-[44px] rounded-[10px] border border-gray-300 px-3 text-sm text-gray-900 focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15 focus:outline-none disabled:bg-gray-50';
const fieldLabel = 'block text-[12.5px] font-semibold text-gray-700 mb-1.5';

export default function VideoTasksTab() {
  const [params, setParams] = useSearchParams();
  const deepLinkHandled = useRef(false);

  const [assignments, setAssignments] = useState<VideoAssignment[]>([]);
  const [counts, setCounts] = useState<VideoTaskCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [filter, setFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [modules, setModules] = useState<ModuleWithChapters[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [reviewId, setReviewId] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [pointDraft, setPointDraft] = useState('');
  const [busy, setBusy] = useState<'save' | 'assign' | 'cancel' | null>(null);
  const [formError, setFormError] = useState('');
  const [formNotice, setFormNotice] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  const editing = editingId ? assignments.find((a) => a.assignmentId === editingId) ?? null : null;
  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));

  const load = async () => {
    setListError('');
    try {
      const res = await videoTasksApi.list();
      setAssignments(res.assignments);
      setCounts(res.counts);
      return res.assignments;
    } catch (e: any) {
      setListError(e.message || 'Could not load video tasks.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load().then((rows) => {
      // `?task=asg_…` from the admin email: open that task once.
      const id = params.get('task');
      if (deepLinkHandled.current || !id) return;
      deepLinkHandled.current = true;
      const a = rows.find((x) => x.assignmentId === id);
      if (a) openRow(a);
    });
    academyApi.listModules().then((r) => setModules(r.modules)).catch(() => setModules([]));
    api.tenants.getAll().then((t) => setTenants(Array.isArray(t) ? t : [])).catch(() => setTenants([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Users of the chosen tenant, for the second "Assign to" dropdown.
  useEffect(() => {
    const tid = form.tenantId;
    if (!tid) { setUsers([]); return; }
    let live = true;
    setUsersLoading(true);
    api.users.getByTenant(tid)
      .then((u) => { if (live) setUsers(Array.isArray(u) ? u : []); })
      .catch(() => { if (live) setUsers([]); })
      .finally(() => { if (live) setUsersLoading(false); });
    return () => { live = false; };
  }, [form.tenantId]);

  const tenantName = (id?: string | null) => {
    if (!id) return '';
    const t = tenants.find((x) => x.id === id);
    return t?.name || t?.businessName || id;
  };

  const targetLabel = (a: VideoAssignment) => {
    if (!a.target) return '';
    const m = modules.find((x) => x.module.moduleId === a.target!.moduleId);
    if (!m) return '';
    const c = a.target.chapterId ? m.chapters.find((x) => x.chapterId === a.target!.chapterId) : undefined;
    return c ? `${m.module.title} › ${c.title}` : m.module.title;
  };

  const setTaskParam = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('task', id); else next.delete('task');
    setParams(next, { replace: true });
  };

  function openRow(a: VideoAssignment) {
    setFormError(''); setFormNotice('');
    if (REVIEWABLE.has(a.status)) {
      setReviewId(a.assignmentId);
    } else {
      setEditingId(a.assignmentId);
      setForm(formFrom(a));
      setPointDraft('');
    }
    setTaskParam(a.assignmentId);
  }

  const resetForm = () => {
    setEditingId(null); setForm(EMPTY_FORM); setPointDraft(''); setFormError(''); setFormNotice('');
    setTaskParam(null);
  };

  const upsert = (a: VideoAssignment) =>
    setAssignments((prev) => (prev.some((x) => x.assignmentId === a.assignmentId)
      ? prev.map((x) => (x.assignmentId === a.assignmentId ? a : x))
      : [a, ...prev]));

  const rewardValid = /^\d+$/.test(form.rewardTokens.trim());
  const payload = (): Partial<VideoAssignment> => ({
    title: form.title.trim(),
    brief: form.brief.trim(),
    keyPoints: form.keyPoints,
    surface: form.surface,
    ...(form.moduleId ? { target: { moduleId: form.moduleId, ...(form.chapterId ? { chapterId: form.chapterId } : {}) } } : {}),
    ...(dueFromDate(form.dueDate) ? { dueAt: dueFromDate(form.dueDate) } : {}),
    rewardTokens: rewardValid ? parseInt(form.rewardTokens.trim(), 10) : 0,
  });

  /** create (new) or update (existing); returns the saved row. */
  const persist = async (): Promise<VideoAssignment> => {
    if (editingId) {
      const { assignment } = await videoTasksApi.update(editingId, payload());
      return assignment;
    }
    const { assignment } = await videoTasksApi.create(payload());
    return assignment;
  };

  const validate = (needAssignee: boolean): string => {
    if (!form.title.trim()) return 'Give the topic a title.';
    if (!rewardValid) return 'Ruby Tokens must be a whole number, 0 or more.';
    if (needAssignee && (!form.tenantId || !form.userId)) return 'Choose a tenant and a user to assign it to.';
    return '';
  };

  const saveDraft = async () => {
    const v = validate(false);
    if (v) { setFormError(v); return; }
    setBusy('save'); setFormError(''); setFormNotice('');
    try {
      const a = await persist();
      upsert(a);
      setEditingId(a.assignmentId);
      setForm(formFrom(a));
      setTaskParam(a.assignmentId);
      setFormNotice('Saved.');
      void load();
    } catch (e: any) {
      setFormError(e.message || 'Could not save.');
    } finally {
      setBusy(null);
    }
  };

  const assignAndNotify = async () => {
    const v = validate(true);
    if (v) { setFormError(v); return; }
    const user = users.find((u) => u.id === form.userId);
    const prior = editing?.assignee;
    const assignee = {
      tenantId: form.tenantId!,
      userId: form.userId!,
      email: user?.email || (prior?.userId === form.userId ? prior.email : '') || '',
      name: user?.name || (prior?.userId === form.userId ? prior.name : '') || '',
    };
    setBusy('assign'); setFormError(''); setFormNotice('');
    let saved: VideoAssignment | null = null;
    try {
      saved = await persist();
      upsert(saved);
      setEditingId(saved.assignmentId);
      const { assignment } = await videoTasksApi.assign(saved.assignmentId, assignee, dueFromDate(form.dueDate));
      upsert(assignment);
      setForm(formFrom(assignment));
      setTaskParam(assignment.assignmentId);
      setFormNotice(`Assigned to ${assignee.name || assignee.email || 'the user'} — they have been emailed.`);
      void load();
    } catch (e: any) {
      setFormError(saved
        ? `Saved as a draft, but not assigned: ${e.message || 'unknown error'}`
        : e.message || 'Could not assign.');
    } finally {
      setBusy(null);
    }
  };

  const cancelTask = async () => {
    if (!editingId) return;
    setBusy('cancel'); setFormError('');
    try {
      const { assignment } = await videoTasksApi.cancel(editingId);
      upsert(assignment);
      setShowCancel(false);
      resetForm();
      void load();
    } catch (e: any) {
      setFormError(e.message || 'Could not cancel this task.');
      setShowCancel(false);
    } finally {
      setBusy(null);
    }
  };

  const addPoint = () => {
    const p = pointDraft.trim();
    if (!p) return;
    if (!form.keyPoints.includes(p)) patch({ keyPoints: [...form.keyPoints, p] });
    setPointDraft('');
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignments.filter((a) => {
      if (filter && a.status !== filter) return false;
      if (!q) return true;
      return [a.title, a.assignee?.name, a.assignee?.email, tenantName(a.assignee?.tenantId)]
        .some((s) => (s || '').toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, filter, search, tenants]);

  const moduleOptions = modules.map((m) => ({ value: m.module.moduleId, label: m.module.title }));
  const chapterOptions = (modules.find((m) => m.module.moduleId === form.moduleId)?.chapters ?? [])
    .map((c) => ({ value: c.chapterId, label: c.title }));
  const tenantOptions = tenants.map((t) => ({ value: t.id, label: t.name || t.businessName || t.id, subtitle: t.id }));
  const userOptions: Array<{ value: string; label: string; subtitle?: string }> =
    users.map((u) => ({ value: u.id, label: u.name || u.email, subtitle: u.role }));
  // Keep the current assignee selectable even before (or without) the user list.
  if (form.userId && !userOptions.some((o) => o.value === form.userId) && editing?.assignee?.userId === form.userId) {
    userOptions.push({ value: form.userId, label: editing.assignee.name || editing.assignee.email || form.userId });
  }

  if (reviewId) {
    return (
      <VideoTaskReview
        assignmentId={reviewId}
        tenantName={tenantName}
        onBack={() => { setReviewId(null); setTaskParam(null); void load(); }}
        onChanged={upsert}
      />
    );
  }

  const locked = !!editing && LOCKED.has(editing.status);
  const canAssign = !editing || ASSIGNABLE.has(editing.status);

  const stats = [
    { label: 'Open tasks', value: counts?.open, color: 'text-gray-900' },
    { label: 'Waiting for your review', value: counts?.waitingReview, color: 'text-[#DC143C]' },
    { label: 'Overdue', value: counts?.overdue, color: 'text-amber-700' },
    { label: 'Approved this month', value: counts?.approvedThisMonth, color: 'text-emerald-700' },
  ];

  return (
    <div className="flex flex-col xl:flex-row gap-6 items-start">
      {/* List */}
      <div className="flex-1 min-w-0 w-full space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="border border-gray-200 rounded-xl px-4 py-3.5">
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value ?? '—'}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => {
              const on = filter === f.id;
              return (
                <button
                  key={f.label}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  aria-pressed={on}
                  className={`min-h-[44px] px-3.5 rounded-full text-[12.5px] transition ${
                    on ? 'bg-gray-900 text-white font-semibold' : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 border border-gray-200 rounded-[10px] px-3 h-11 w-full sm:w-[260px] text-gray-400 focus-within:border-[#DC143C]">
            <Search className="w-4 h-4 flex-shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search topics, tenants, people"
              className="flex-1 min-w-0 bg-transparent text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none"
            />
          </label>
        </div>

        {listError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{listError}</p>
        )}

        <div className="border border-gray-200 rounded-[14px] overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-[2.2fr_1fr_1.6fr_0.9fr_1fr_0.8fr] gap-3 px-4 py-3 bg-gray-50 text-[11.5px] font-bold text-gray-500 uppercase tracking-wide">
              <span>Topic</span><span>App</span><span>Assigned to</span><span>Due</span><span>Status</span><span>Reward</span>
            </div>
            {loading && <RubyLoader variant="section" label="Loading video tasks…" />}
            {!loading && visible.length === 0 && (
              <p className="text-sm text-gray-500 px-4 py-6 border-t border-gray-100">
                {assignments.length === 0 ? 'No video tasks yet — create one on the right.' : 'Nothing matches this filter.'}
              </p>
            )}
            {visible.map((a) => {
              const overdue = isOverdue(a);
              const on = editingId === a.assignmentId;
              return (
                <button
                  key={a.assignmentId}
                  type="button"
                  onClick={() => openRow(a)}
                  className={`w-full text-left grid grid-cols-[2.2fr_1fr_1.6fr_0.9fr_1fr_0.8fr] gap-3 px-4 py-3.5 border-t border-gray-100 items-center text-[13.5px] min-h-[60px] transition ${
                    on ? 'bg-[#FFF1F3]' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-semibold text-gray-900 truncate">{a.title}</span>
                    <span className="text-xs text-gray-500 truncate">{targetLabel(a) || '—'}</span>
                  </span>
                  <span className="text-[12.5px] text-gray-700">{SURFACE_LABEL[a.surface]}</span>
                  <span className="flex flex-col gap-0.5 min-w-0">
                    <span className="truncate text-gray-900">{a.assignee ? (a.assignee.name || a.assignee.email || a.assignee.userId) : '—'}</span>
                    <span className="text-xs text-gray-500 truncate">
                      {a.assignee ? tenantName(a.assignee.tenantId) : 'Unassigned draft'}
                    </span>
                  </span>
                  <span className={`text-[12.5px] ${overdue ? 'text-amber-700 font-semibold' : a.dueAt ? 'text-gray-700' : 'text-gray-400'}`}>
                    {formatDue(a.dueAt)}
                  </span>
                  <span>
                    <span className={`inline-flex w-fit text-[11.5px] font-bold px-2.5 py-1 rounded-full ${STATUS_TONE[a.status]}`}>
                      {STATUS_LABEL[a.status]}
                    </span>
                  </span>
                  <span className="text-[12.5px] text-gray-700">
                    {a.rewardTokens > 0 ? `${a.rewardTokens.toLocaleString()} tokens` : '—'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* New / edit topic */}
      <aside className="w-full xl:w-[440px] flex-shrink-0 border border-gray-200 rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden bg-white">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-[17px] font-bold text-gray-900">{editing ? 'Edit video topic' : 'New video topic'}</h2>
            {editing && (
              <span className={`inline-flex mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[editing.status]}`}>
                {STATUS_LABEL[editing.status]}
              </span>
            )}
          </div>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              aria-label="Close and start a new topic"
              className="w-11 h-11 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="px-5 py-4 space-y-3.5">
          <div>
            <label className={fieldLabel} htmlFor="vt-title">Title</label>
            <input
              id="vt-title"
              value={form.title}
              disabled={locked}
              onChange={(e) => patch({ title: e.target.value })}
              placeholder="How to receive stock with a GRN"
              className={inputCls}
            />
          </div>

          <div>
            <label className={fieldLabel} htmlFor="vt-brief">Brief — what to explain</label>
            <textarea
              id="vt-brief"
              value={form.brief}
              disabled={locked}
              onChange={(e) => patch({ brief: e.target.value })}
              rows={4}
              className={`${inputCls} py-2.5 resize-none leading-snug`}
            />
          </div>

          <div>
            <span className={fieldLabel}>Key points to cover</span>
            <div className="flex flex-wrap gap-1.5">
              {form.keyPoints.map((p) => (
                <span key={p} className="inline-flex items-center gap-1 text-xs pl-2.5 pr-1 min-h-[32px] rounded-full bg-[#FFF1F3] text-[#9F1239]">
                  {p}
                  {!locked && (
                    <button
                      type="button"
                      aria-label={`Remove ${p}`}
                      onClick={() => patch({ keyPoints: form.keyPoints.filter((x) => x !== p) })}
                      className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#DC143C]/10"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
              {!locked && (
                <input
                  value={pointDraft}
                  onChange={(e) => setPointDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPoint(); } }}
                  onBlur={addPoint}
                  placeholder="+ Add (Enter)"
                  aria-label="Add a key point"
                  className="text-xs px-2.5 min-h-[32px] w-32 rounded-full border border-dashed border-gray-300 text-gray-700 placeholder:text-gray-500 focus:outline-none focus:border-[#DC143C]"
                />
              )}
            </div>
          </div>

          <div>
            <span className={fieldLabel}>Record in</span>
            <div className="flex bg-gray-100 rounded-[10px] p-1 gap-1" role="radiogroup" aria-label="Record in">
              {(['platform-app', 'pos'] as const).map((s) => {
                const on = form.surface === s;
                return (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={locked}
                    onClick={() => patch({ surface: s })}
                    className={`flex-1 min-h-[40px] rounded-lg text-[13px] transition ${
                      on ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    {s === 'pos' ? 'POS (web till)' : 'Platform app'}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className={fieldLabel}>Publishes into</span>
            <div className="grid grid-cols-2 gap-2.5">
              <SearchableDropdown
                ariaLabel="Module"
                options={moduleOptions}
                value={form.moduleId}
                disabled={locked}
                onChange={(v) => patch({ moduleId: v, chapterId: null })}
                placeholder="Module…"
              />
              <SearchableDropdown
                ariaLabel="Chapter"
                options={chapterOptions}
                value={form.chapterId}
                disabled={locked || !form.moduleId}
                onChange={(v) => patch({ chapterId: v })}
                placeholder="Chapter…"
              />
            </div>
          </div>

          <div>
            <span className={fieldLabel}>Due</span>
            <DatePicker
              value={form.dueDate}
              disabled={locked}
              onChange={(v) => patch({ dueDate: v })}
              placeholder="No due date"
              className="!h-11 !rounded-[10px]"
            />
          </div>

          <div>
            <span className={fieldLabel}>Assign to</span>
            <div className="grid grid-cols-2 gap-2.5">
              <SearchableDropdown
                ariaLabel="Tenant"
                options={tenantOptions}
                value={form.tenantId}
                disabled={locked || !canAssign}
                onChange={(v) => patch({ tenantId: v, userId: null })}
                placeholder="Tenant…"
                searchPlaceholder="Search tenants…"
              />
              <SearchableDropdown
                ariaLabel="User"
                options={userOptions}
                value={form.userId}
                disabled={locked || !canAssign || !form.tenantId}
                onChange={(v) => patch({ userId: v })}
                placeholder={usersLoading ? 'Loading users…' : 'User…'}
                searchPlaceholder="Search users…"
              />
            </div>
            <p className="text-[11.5px] text-gray-500 mt-1.5 flex items-center gap-1.5">
              {usersLoading && <RubyGemMark size={12} drawing />}
              Tenant first, then a user in it. Merchant login — they record in their own store.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 bg-gray-50 rounded-[10px] px-3 py-2.5">
            <div className="min-w-0">
              <label htmlFor="vt-reward" className="block text-[13px] font-semibold text-gray-900">Ruby Tokens offered</label>
              <span className="text-[11.5px] text-gray-500">Shown in the email · granted after approval</span>
            </div>
            <input
              id="vt-reward"
              inputMode="numeric"
              value={form.rewardTokens}
              disabled={locked}
              onChange={(e) => patch({ rewardTokens: e.target.value.replace(/[^\d]/g, '') })}
              className="w-28 min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 text-sm text-right focus:border-[#DC143C] focus:outline-none"
            />
          </div>

          {formError && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
          )}
          {formNotice && !formError && (
            <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{formNotice}</p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex flex-wrap items-center gap-2.5 justify-end">
          {editing && !locked && (
            <button
              type="button"
              onClick={() => setShowCancel(true)}
              disabled={!!busy}
              className="mr-auto inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-[10px] text-sm text-red-600 hover:bg-red-50 disabled:opacity-40"
            >
              <Ban className="w-4 h-4" /> Cancel task
            </button>
          )}
          <button
            type="button"
            onClick={() => void saveDraft()}
            disabled={!!busy || locked}
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] border border-gray-300 bg-white text-sm text-gray-900 hover:border-gray-900 disabled:opacity-40"
          >
            {busy === 'save' && <RubyGemMark size={16} drawing color="currentColor" />}
            {editing && editing.status !== 'draft' ? 'Save changes' : 'Save draft'}
          </button>
          {canAssign && (
            <button
              type="button"
              onClick={() => void assignAndNotify()}
              disabled={!!busy || locked}
              className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-[10px] bg-[#DC143C] text-white text-sm font-semibold hover:bg-[#9F1239] disabled:opacity-40"
            >
              {busy === 'assign' && <RubyGemMark size={16} drawing color="currentColor" />}
              Assign &amp; notify
            </button>
          )}
        </div>
      </aside>

      <ConfirmDialog
        open={showCancel}
        title="Cancel this video task?"
        tone="danger"
        confirmLabel="Cancel task"
        busy={busy === 'cancel'}
        onConfirm={() => void cancelTask()}
        onClose={() => setShowCancel(false)}
      >
        <p>The assignee can no longer record or submit it. This cannot be undone — create a new topic to ask again.</p>
      </ConfirmDialog>
    </div>
  );
}
